import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play, Pause, SkipBack, SkipForward, ListMusic, X, Headphones, Loader2, RotateCcw,
  Gauge, MoonStar, Download, CheckCircle2, AlertTriangle, RefreshCw, WifiOff,
} from "lucide-react";
import { useAudiobook } from "@/context/AudiobookContext";
import { useLang } from "@/context/LangContext";
import { AUDIOBOOK_UI as UI } from "@/data/audiobookI18n";
import { LANG_TAG } from "@/lib/geo";
import { fill } from "@/lib/utils";

const IconBtn = ({ onClick, testId, label, disabled, active, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    aria-label={label}
    title={label}
    className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors disabled:opacity-30 ${active ? "bg-[hsl(var(--gold))]/25 text-[hsl(var(--gold))]" : "text-white/80 hover:bg-white/10"}`}
  >
    {children}
  </button>
);

const Chip = ({ onClick, testId, label, active, warn, children }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    aria-label={label}
    title={label}
    className={`inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium transition-colors ${
      warn ? "bg-amber-400/15 text-amber-300" : active ? "bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]" : "bg-white/10 text-white/75 hover:bg-white/15"
    }`}
  >
    {children}
  </button>
);

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
export const fmtEta = (sec, tc) => (sec == null ? "" : sec >= 60 ? fill(tc(UI.minutesShort), { n: Math.ceil(sec / 60) }) : fill(tc(UI.secondsShort), { n: Math.max(5, Math.round(sec / 5) * 5) }));
export const fmtRate = (r, lang) => `${r.toLocaleString(LANG_TAG[lang] || "en-US", { maximumFractionDigits: 2 })}×`;

// Persistent bottom-sheet player for the Audio Handbook (rendered once in Layout).
export const AudiobookPlayer = () => {
  const ab = useAudiobook();
  const { tc, lang } = useLang();
  const [showList, setShowList] = useState(false);
  const [showSleep, setShowSleep] = useState(false);
  const { open, status, pos, chapters, offline } = ab;
  const ch = chapters[pos.c];
  const total = chapters.reduce((n, c) => n + c.segments.length, 0);
  const done = chapters.slice(0, pos.c).reduce((n, c) => n + c.segments.length, 0) + pos.s;
  const pct = status === "finished" ? 100 : Math.round((done / total) * 100);
  const statusLine = {
    loading: tc(UI.loading), playing: tc(UI.nowReading), paused: tc(UI.paused),
    finished: tc(UI.finished), quota: tc(UI.quotaHit), error: tc(UI.error),
  }[status] || "";
  const warn = status === "quota" || status === "error";

  const sleepLabel = ab.sleep?.mode === "min" ? fill(tc(UI.sleepActive), { t: mmss(ab.sleepLeft) })
    : ab.sleep?.mode === "chapter" ? tc(UI.sleepChapterActive) : tc(UI.sleepTimer);

  const dlStatus = offline.dl?.lang === lang ? offline.dl.status : null;
  const eta = offline.downloading && offline.dl.etaSec != null ? ` · ${fill(tc(UI.eta), { t: fmtEta(offline.dl.etaSec, tc) })}` : "";
  const dlLabel = offline.downloading ? fill(tc(UI.downloading), { done: offline.dl.done, total: offline.dl.total }) + eta
    : offline.waiting ? tc(UI.waitingNet)
    : dlStatus === "quota" ? tc(UI.downloadQuota)
    : dlStatus === "error" ? tc(UI.downloadError)
    : offline.state === "done" ? tc(UI.downloaded)
    : offline.state === "stale" ? tc(UI.downloadStale)
    : offline.state === "partial" ? fill(tc(UI.downloadPartial), { done: offline.done, total: offline.total })
    : tc(UI.downloadPack);
  const onDownload = () => {
    if (offline.downloading || offline.waiting) ab.cancelDownload();
    else if (offline.state !== "done") ab.startDownload();
  };

  return (
    <AnimatePresence>
      {open && ch && (
        <motion.div
          initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 90, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          className="fixed inset-x-0 bottom-24 md:bottom-4 z-50 px-3"
          data-testid="audiobook-player"
        >
          <div className="mx-auto max-w-2xl overflow-hidden rounded-3xl bg-[#0b3d2c] text-white shadow-2xl ring-1 ring-[hsl(var(--gold))]/40">
            {showList && (
              <div className="max-h-56 overflow-y-auto border-b border-white/10 p-2" data-testid="audiobook-chapter-list">
                {chapters.map((c, i) => (
                  <button
                    key={c.id}
                    onClick={() => { ab.jump(i); setShowList(false); }}
                    data-testid={`audiobook-chapter-${i}`}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-start text-sm transition-colors ${
                      i === pos.c ? "bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]" : "text-white/85 hover:bg-white/5"
                    }`}
                  >
                    <span className="w-6 shrink-0 text-xs tabular-nums opacity-70">{i + 1}</span>
                    <span className="truncate">{c.title}</span>
                  </button>
                ))}
              </div>
            )}
            {showSleep && (
              <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-3 py-2.5" data-testid="audiobook-sleep-menu">
                <span className="me-1 inline-flex items-center gap-1 text-[11px] text-white/60"><MoonStar className="h-3.5 w-3.5" /> {tc(UI.sleepTimer)}</span>
                {[15, 30, 45, 60].map((m) => (
                  <Chip key={m} onClick={() => { ab.setSleepMinutes(m); setShowSleep(false); }} testId={`audiobook-sleep-${m}`} label={fill(tc(UI.minutes), { n: m })}>
                    {fill(tc(UI.minutes), { n: m })}
                  </Chip>
                ))}
                <Chip onClick={() => { ab.setSleepChapter(); setShowSleep(false); }} testId="audiobook-sleep-chapter" label={tc(UI.sleepEndChapter)} active={ab.sleep?.mode === "chapter"}>
                  {tc(UI.sleepEndChapter)}
                </Chip>
                {ab.sleep && (
                  <Chip onClick={() => { ab.clearSleep(); setShowSleep(false); }} testId="audiobook-sleep-off" label={tc(UI.sleepOff)} warn>
                    <X className="h-3 w-3" /> {tc(UI.sleepOff)}
                  </Chip>
                )}
              </div>
            )}
            <div className="h-1 w-full bg-white/10">
              <div className="h-full bg-[hsl(var(--gold))] transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="audiobook-progress" />
            </div>
            <div className="flex items-center gap-3 p-3 pb-2 sm:px-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))]">
                {status === "playing" ? (
                  <span className="ab-bars flex h-5 items-end gap-[3px]" aria-hidden><span /><span /><span /><span /></span>
                ) : (
                  <Headphones className="h-5 w-5" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--gold))]" data-testid="audiobook-chapter-of">
                  {fill(tc(UI.chapterOf), { n: pos.c + 1, total: chapters.length })}
                </p>
                <p className="truncate text-sm font-medium" data-testid="audiobook-chapter-title">{ch.title}</p>
                <p className={`truncate text-[11px] ${warn ? "text-amber-300" : "text-white/60"}`} data-testid="audiobook-status">{statusLine}</p>
              </div>
              <div className="flex items-center gap-0.5 sm:gap-1">
                <IconBtn onClick={ab.prev} testId="audiobook-prev" label={tc(UI.prev)} disabled={pos.c === 0}>
                  <SkipBack className="h-4 w-4 rtl:-scale-x-100" />
                </IconBtn>
                <button
                  onClick={ab.toggle}
                  disabled={status === "loading"}
                  data-testid="audiobook-toggle"
                  aria-label={status === "playing" ? tc(UI.pause) : status === "finished" ? tc(UI.restart) : tc(UI.play)}
                  className="mx-0.5 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--gold))] text-[#0b3d2c] shadow-lg transition-transform hover:scale-105 disabled:opacity-70"
                >
                  {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" />
                    : status === "playing" ? <Pause className="h-5 w-5" />
                    : status === "finished" ? <RotateCcw className="h-5 w-5" />
                    : <Play className="ms-0.5 h-5 w-5" />}
                </button>
                <IconBtn onClick={ab.next} testId="audiobook-next" label={tc(UI.next)} disabled={pos.c >= chapters.length - 1}>
                  <SkipForward className="h-4 w-4 rtl:-scale-x-100" />
                </IconBtn>
                <IconBtn onClick={() => { setShowSleep(false); setShowList((v) => !v); }} testId="audiobook-list" label={tc(UI.chapters)} active={showList}>
                  <ListMusic className="h-4 w-4" />
                </IconBtn>
                <IconBtn onClick={() => { setShowList(false); setShowSleep(false); ab.close(); }} testId="audiobook-close" label={tc(UI.close)}>
                  <X className="h-4 w-4" />
                </IconBtn>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 px-3 pb-3 sm:px-4" data-testid="audiobook-tools">
              <Chip onClick={ab.cycleRate} testId="audiobook-rate" label={tc(UI.speed)} active={ab.rate !== 1}>
                <Gauge className="h-3.5 w-3.5" /> <span className="tabular-nums" data-testid="audiobook-rate-value">{fmtRate(ab.rate, lang)}</span>
              </Chip>
              <Chip onClick={() => { setShowList(false); setShowSleep((v) => !v); }} testId="audiobook-sleep" label={tc(UI.sleepTimer)} active={!!ab.sleep || showSleep}>
                <MoonStar className="h-3.5 w-3.5" /> <span className="tabular-nums" data-testid="audiobook-sleep-value">{sleepLabel}</span>
              </Chip>
              <Chip onClick={onDownload} testId="audiobook-download" label={dlLabel} active={offline.state === "done"} warn={offline.waiting || dlStatus === "quota" || dlStatus === "error" || offline.state === "stale"}>
                {offline.downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : offline.waiting ? <WifiOff className="h-3.5 w-3.5" />
                  : offline.state === "done" ? <CheckCircle2 className="h-3.5 w-3.5" />
                  : offline.state === "stale" ? <RefreshCw className="h-3.5 w-3.5" />
                  : dlStatus ? <AlertTriangle className="h-3.5 w-3.5" />
                  : <Download className="h-3.5 w-3.5" />}
                <span className="truncate max-w-[260px]" data-testid="audiobook-download-label" data-state={offline.downloading ? "downloading" : offline.waiting ? "waiting" : dlStatus || offline.state}>{dlLabel}</span>
              </Chip>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
